import React from 'react';

/**
 * The five-step loop, drawn once.
 *
 * Identify → Reach → Verify → Route and book → Confirm, with the confirmation
 * running back to whoever started the referral. The same five steps the
 * UIS-to-FFS deck arrived at independently for a member in Fresno, because
 * they are the product's actual shape rather than a marketing device.
 *
 * Hand-written SVG committed to the repo rather than an export from a design
 * tool, so it inherits light and dark themes and stays editable by whoever is
 * editing the copy. `labels` lets each audience relabel the steps without a
 * second drawing.
 */
export interface LoopStep {
  name: string;
  detail: string;
}

export const DEFAULT_STEPS: LoopStep[] = [
  { name: 'Identify', detail: 'Who needs help, and what for' },
  { name: 'Reach', detail: 'In their language, on the number they answer' },
  { name: 'Verify', detail: 'Confirm coverage before anyone is sent anywhere' },
  { name: 'Route & book', detail: 'To an organization that is near and able to help' },
  { name: 'Confirm', detail: 'What was delivered, and by whom' },
];

interface ClosedLoopProps {
  steps?: LoopStep[];
  heading?: string;
  lede?: string;
  /** Emphasise one step (0-based). Defaults to Confirm, the one that is missing today. */
  highlight?: number;
}

const ClosedLoop: React.FC<ClosedLoopProps> = ({
  steps = DEFAULT_STEPS,
  heading = 'How one referral actually works',
  lede = 'Five steps. The last one is the step that does not exist today.',
  highlight = 4,
}) => (
  <section className="py-16 bg-white dark:bg-gray-950">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3 text-center">{heading}</h2>
      <p className="text-base text-gray-600 dark:text-gray-300 text-center max-w-2xl mx-auto mb-12">
        {lede}
      </p>

      <ol className="relative space-y-4">
        {steps.map((step, i) => {
          const isHighlight = i === highlight;
          return (
            <li
              key={step.name}
              className={`relative flex items-start gap-4 rounded-xl border p-5 ${
 isHighlight
 ? 'border-v2accent bg-v2accent-soft'
 : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
 }`}
            >
              <span
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
 isHighlight
 ? 'bg-v2accent text-white'
 : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
 }`}
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100">{step.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{step.detail}</p>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-6 flex items-start gap-3 rounded-xl border border-dashed border-v2accent p-5">
        <svg
          viewBox="0 0 24 24"
          className="w-6 h-6 flex-shrink-0 text-v2accent"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <path d="M20 5v6h-6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M20 11a8 8 0 1 0-2.3 5.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          <strong className="font-semibold">The loop closes here.</strong> Step 5 goes back to
          whoever started the referral. Without it, nobody finds out whether the person was
          helped — and the first news is often the next emergency department visit.
        </p>
      </div>
    </div>
  </section>
);

export default ClosedLoop;
